import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen,
  Briefcase,
  Building2,
  FileSignature,
  Plane,
  ShieldCheck,
  Calculator,
  KeyRound,
  TrendingUp,
  ClipboardList,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/lib/data";
import useSeo from "@/hooks/useSeo";

const ICONS = { BookOpen, Briefcase, Building2, FileSignature, Plane, ShieldCheck, Calculator, KeyRound, TrendingUp, ClipboardList, Sparkles };

export default function ServicesPage() {
  useSeo({
    title: "Services",
    description:
      "Explore ConnectHER's full service ecosystem — bookkeeping, taxes, business formation, credit repair, notary, travel, consulting, and more. One trusted partner.",
    path: "/services",
  });
  return (
    <>
      <section className="pt-16 md:pt-24 pb-10" data-testid="services-hero">
        <div className="container-app max-w-4xl">
          <div className="eyebrow">Our Services</div>
          <h1 className="mt-3 text-5xl md:text-6xl leading-tight">
            Professional services designed to support{" "}
            <span className="italic text-gold">
              financial wellness, business growth, and personal success.
            </span>
          </h1>
          <p className="mt-6 text-lg text-navy/70 max-w-2xl leading-relaxed">
            Each ConnectHER service stands on its own — and stacks beautifully
            when combined. Choose what you need now; we&apos;ll help you grow
            into the rest.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-app grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const accent =
              s.color === "gold" ? "bg-gold" : s.color === "teal" ? "bg-teal" : "bg-navy";
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-card-${s.slug}`}
                  className="card block overflow-hidden group h-full"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                    />
                    <div className={`absolute top-4 left-4 w-10 h-10 rounded-xl ${accent} text-white flex items-center justify-center`}>
                      <Icon className="w-5 h-5" strokeWidth={1.8} />
                    </div>
                    {s.comingSoon && (
                      <div className="absolute top-4 right-4 bg-white/95 text-navy text-[10px] uppercase tracking-widest px-3 py-1 rounded-full shadow">
                        Coming Soon
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl mb-2">{s.name}</h3>
                    <p className="text-sm text-navy/65 leading-relaxed">{s.tagline}</p>
                    <div className="mt-5 pt-4 border-t border-navy/5 flex items-center justify-between">
                      <span className="text-xs uppercase tracking-widest text-gold">{s.price_label}</span>
                      <ArrowUpRight className="w-4 h-4 text-navy/40 group-hover:text-gold transition" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>
    </>
  );
}
